import type { HTMLAttributes } from 'react';
import { cn } from '../cn.js';

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent';

const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-bg-subtle text-fg-muted border-border',
  success: 'bg-success-subtle text-success border-transparent',
  warning: 'bg-warning-subtle text-warning border-transparent',
  danger: 'bg-danger-subtle text-danger border-transparent',
  info: 'bg-info-subtle text-info border-transparent',
  accent: 'bg-accent-subtle text-accent border-transparent',
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  /** Renders on the hero gradient rather than a plain surface — same convention as
   *  `Avatar.onHero`. Forces a translucent-white pill regardless of `tone`: a neutral/info tone's
   *  background is `--bg-subtle`/`--info-subtle`, which flips dark under the dark theme and would
   *  otherwise render near-invisible text against the hero gradient (a fixed brand colour that does
   *  NOT flip with the theme — see tokens.css's `--hero-gradient` comment). Only meant for a plain
   *  informational chip (a scope/cycle label); a real status tone (success/warning/danger) already
   *  reads fine on the hero since those subtle backgrounds stay pale in both themes. */
  onHero?: boolean;
}

export function Badge({
  tone = 'neutral',
  onHero = false,
  className,
  children,
  ...rest
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        onHero ? 'border-white/30 bg-white/15 text-white' : TONES[tone],
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}

/**
 * Status colour must never be the ONLY signal.
 *
 * Roughly 1 in 12 men has a colour-vision deficiency, and this maps leave and payroll states that
 * people act on. Every tone therefore pairs with a label, and this helper keeps the pairing in one
 * place so a caller cannot accidentally ship a bare coloured dot.
 */
/**
 * Excludes `accent`: `apps/web/src/styles.css` imports shadcn's token set, which declares its own
 * `--accent` on `:root` (a pale highlight colour, shadcn's own convention) — and because it's
 * imported after `@hrms/ui`'s tokens.css on the same selector, it silently overwrites this
 * package's brand-blue `--accent`, making `text-accent` on `bg-accent-subtle` render as
 * (near-)invisible text everywhere in apps/web (buttons, active nav, and this hash). That is an
 * app-wide token-collision bug for a follow-up, not something a badge helper should paper over by
 * guessing — but it also has no fix available from inside this package, so avoiding the one tone
 * it breaks is the honest mitigation here.
 */
const HASH_TONES: readonly BadgeTone[] = ['success', 'warning', 'danger', 'info'];

/**
 * A consistent, arbitrary colour for a string with no fixed enum behind it — a department name, a
 * shift name, anything org-defined data rather than one of our own statuses. Deliberately excludes
 * `neutral`: the point is visual distinction between values, and every value landing on the same
 * grey defeats that. Same string always maps to the same tone (a stable sum over char codes, not
 * `Math.random()`), so a department's colour does not shuffle on every render or reload.
 */
export function hashTone(value: string): BadgeTone {
  let sum = 0;
  for (let i = 0; i < value.length; i++) sum += value.charCodeAt(i);
  return HASH_TONES[sum % HASH_TONES.length]!;
}

/**
 * Training statuses (`TrainingAssignmentStatus`/`TrainingDisplayStatus`/`TrainingVerificationStatus`
 * in `@hrms/shared`'s `enums.ts`) are folded into this same switch rather than given a second
 * helper, per Training_Module_Plan.md §6.2 — `not_started`/`started`/`in_progress`/`completed_late`/
 * `incomplete`/`due_soon`/`verified`/`gaps_detected`/`not_recorded`/`waived` below.
 *
 * `completed` is DELIBERATELY NOT added here, even though the training plan calls for
 * `completed` → `success`. Two live callers already pass their own `completed` value through this
 * exact function and rely on today's fallthrough to `info`:
 * `apps/web/src/routes/performance/feedback/OneOnOneDetail.tsx` and `.../OneOnOneList.tsx`, both
 * badging a `OneOnOneStatus` (`ONE_ON_ONE_STATUSES` in enums.ts, unrelated to training). Adding an
 * explicit `case 'completed': return 'success'` here would silently flip those two 1:1-meeting
 * badges from blue to green — a real, unrelated module's rendered colour — which is exactly the
 * kind of shared-helper collision this function's callers need to be able to trust won't happen.
 * Training's own `completed` badge should get its tone from a small local map in the training
 * screens (same pattern as `apps/web/src/routes/performance/reviews/status.ts`'s
 * `reviewStatusTone`), not from a change here. See TG-0.3's handoff notes for the full writeup.
 */
export function statusTone(status: string): BadgeTone {
  switch (status) {
    // ADDED for the agreements/e-signature module — a counter-signed agreement is a success state,
    // same family as 'approved'/'confirmed' above.
    case 'approved':
    case 'confirmed':
    case 'active':
    case 'disbursed':
    case 'settled':
    case 'paid':
    case 'verified':
    case 'signed':
    // ADDED for Travel Desk — a trip the desk has booked, and the desk's own confirmed / refund
    // received outcomes, are the same "done" family as approved / confirmed above.
    case 'booked':
    case 'received':
      return 'success';
    case 'pending':
    case 'in_review':
    case 'probation':
    case 'notice_period':
    case 'on_leave':
    case 'file_generated':
    // ADDED for Travel Desk — someone must act (declined item, expired option, overspend; a
    // booking parked awaiting the traveller).
    case 'needs_action':
    case 'on_hold':
    case 'completed_late':
    case 'incomplete':
    case 'due_soon':
    case 'gaps_detected':
    // ADDED for the platform Companies panel — a signup trial that ran out and was never converted.
    case 'trial_ended':
      return 'warning';
    // ADDED B-P2 — suspended is past_due gone terminal, same severity family.
    // ADDED for the agreements module — a lapsed envelope needs the same "needs attention" red as
    // an outright rejection, not the neutral 'draft' tone.
    case 'rejected':
    case 'cancelled':
    case 'overdue':
    case 'exited':
    case 'past_due':
    case 'suspended':
    case 'written_off':
    case 'expired':
    // ADDED for Travel Desk — a booking the vendor or desk turned down, and a refund that will
    // never arrive.
    case 'declined':
    case 'forfeited':
      return 'danger';
    case 'draft':
    case 'rescinded':
    case 'no_subscription':
    // ADDED for Travel Desk — finished or superseded states with nothing left to do. `withdrawn`
    // previously fell through to the blue default although it is an inert end state (it is shared
    // with expense claims and advances, which read better grey too).
    case 'closed':
    case 'replaced':
    case 'withdrawn':
    case 'not_started':
    case 'not_recorded':
    case 'waived':
      return 'neutral';
    // ADDED for the agreements module — sent/viewed are in-flight, informational states between
    // 'draft' and the 'signed' success state.
    case 'pre_joining':
    case 'started':
    case 'in_progress':
    case 'sent':
    case 'viewed':
    // ADDED for Travel Desk — in-flight states (invited to a trip, options awaiting a choice,
    // travelled and being settled). Explicit rather than left to the default so a later change of
    // the default does not silently recolour them.
    case 'invited':
    case 'options_shared':
    case 'settling':
    // ADDED for the Platform Admin notifications dashboard (NotificationDelivery.status) — queued
    // is in-flight, the same family as sent/viewed above.
    case 'queued':
      return 'info';
    // ADDED for the Platform Admin notifications dashboard — a delivery that failed outright is
    // the same severity as rejected/cancelled above.
    case 'failed':
      return 'danger';
    // Suppressed (a recipient's own preference blocked it) and skipped (no channel available) are
    // not failures — nothing is wrong, delivery just didn't happen — so they read as attention
    // states, not errors.
    case 'suppressed':
      return 'warning';
    case 'skipped':
      return 'neutral';
    default:
      return 'info';
  }
}
